import axios from "axios";
import "./interceptors";
import { Numbers } from "../constants/constant";
import { formatDate } from "../utils/formatDate";
import { toast } from "react-toastify";

// Repointed at the new NestJS API (`/api/v1/admin/*`, `/api/v1/auth/*`) — see
// docs/logs for the full migration notes. Function names/signatures are kept
// stable so the container components didn't need to change; only the URLs
// (and a few payload/response shapes) moved.

/**
 * The new API answers every list as `{ data, meta: { total, page, ... } }`, while
 * the legacy containers read `.users` for the rows and `.totalCounts` for the
 * count. Nothing produced `totalCounts`, so `{totalCounts > 10 && <Pagination/>}`
 * was permanently false: User Management and Podcasts were stuck showing the
 * first ten rows with no way to reach page 2. This bridges both conventions on
 * one object rather than editing every consumer.
 */
function withLegacyMeta(payload) {
  if (!payload || !Array.isArray(payload.data)) return payload;
  return {
    ...payload,
    users: payload.data,
    totalCounts: payload.meta ? payload.meta.total : payload.data.length,
  };
}

export function userLogin(userDetails) {
  return (dispatch) => {
    let data = { email: userDetails.email, password: userDetails.password };
    return axios
      .post(`api/v1/auth/login`, data)
      .then((res) => {
        // New shape: { accessToken, tokenType, expiresIn, user }. Only
        // `user.isAdmin` accounts may use the Admin Site — checked here so a
        // non-staff login fails closed instead of getting 403s screen-by-screen.
        if (!res.data?.user?.isAdmin) {
          return false;
        }
        dispatch({
          type: "SET_AUTH",
          data: { token: res.data.accessToken, user: res.data.user },
        });
        return { token: res.data.accessToken, user: res.data.user };
      })
      .catch((err) => {
        return false;
      });
  };
}

export function userPasswordForgot(userEmail) {
  return (dispatch) => {
    return axios
      .post(`api/v1/auth/password/forgot`, userEmail)
      .then((res) => {
        dispatch({
          type: "USER_FORGOT_PASSWORD",
          data: res,
        });
        return res;
      })
      .catch((err) => {
        return false;
      });
  };
}

export function userList(page, limit, sortBy, sortDir) {
  let url = `api/v1/admin/users?page=${page ? page : Numbers.one}&limit=${
    limit ? limit : Numbers.ten
  }`;
  // Only the Name column is marked `sortable` in Listing.jsx; `sortDir` there
  // is a toggled boolean (`this.state.sort`), not an 'asc'/'desc' string.
  if (sortBy) {
    url += `&sortBy=${sortBy}&sortDir=${sortDir ? "asc" : "desc"}`;
  }
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        dispatch({
          type: "SET_AUTH",
          data: res.data,
        });
        return withLegacyMeta(res.data);
      })
      .catch((err) => {
        return false;
      });
  };
}

export function ChangeUserStatus(user, status) {
  const data = { active: status === "active" ? true : false };
  return (dispatch) => {
    return axios
      .patch(`api/v1/admin/users/${user}`, data)
      .then((res) => {
        dispatch({
          type: "USER_STATUS",
          data: res,
        });
        return res.data;
      })
      .catch((err) => {
        return false;
      });
  };
}

// Complete a password reset from the emailed link.
//
// This was stubbed on the premise that the new API has "no admin equivalent" of
// the reset flow — but staff accounts are ordinary `users` rows with
// `is_admin = true` (that is exactly what AdminGuard checks), so
// `/auth/password/reset` serves them like anyone else. The stub left the Reset
// Password screen's button doing nothing.
//
// The reset token arrives in the link's query string; ResetPassword.jsx parses
// it and stores it under localStorage `token` (the same key the axios
// interceptor sends as the bearer). The new API wants it in the BODY instead,
// so read it back from there — keeping this wrapper's signature stable, per
// this module's contract.
export function userResetPassword(userPassword, isAdmin) {
  return (dispatch) => {
    const token = localStorage.getItem("token");
    if (!token) return Promise.resolve(false);
    return axios
      .post(`api/v1/auth/password/reset`, { token, password: userPassword })
      .then((res) => res && res.status === 200)
      .catch(() => false);
  };
}

export function emailVerification() {
  return (dispatch) => {
    return axios
      .get(`api/v1/auth/email/verify`)
      .then((res) => {
        dispatch({
          type: "EMAIL_VERIFY",
          data: res.data,
        });
        return res.data;
      })
      .catch((err) => {
        return false;
      });
  };
}

export function userDetail(id) {
  return (dispatch) => {
    return axios
      .get(`api/v1/admin/users/${id}`)
      .then((res) => {
        const u = res.data;
        // ListingDetail.jsx reads `data.data` (a nested envelope) and
        // Profile.jsx reads a few legacy snake_case field names directly off
        // it — bridge both here rather than touching those components.
        const shaped = {
          data: {
            ...u,
            profile_image: u.profileImageUrl,
            website_url: u.websiteUrl,
            // Now returned by AdminUserDetail. This used to be hardcoded `[]`
            // because the field did not exist server-side, which silently made
            // the Admin Site's video-link editor unable to prefill.
            video_links: u.videoLinks || [],
            cover_image: u.coverImageUrl,
            video_url: u.videoUrl,
            instagram_url: u.instagramUrl,
            tiktok_url: u.tiktokUrl,
            soundcloud_url: u.soundcloudUrl,
            // ListingDetail.jsx renders an Uploaded Tracks list and a Plan
            // section from these two. Neither existed on the response before,
            // so the track list was always empty (its Remove button pointed at
            // rows staff could never see) and "Total Spent" always blank.
            audio_links: u.audioLinks || [],
            user_plans: u.userPlans
              ? {
                  totalSpent: u.userPlans.totalSpent,
                  data: (u.userPlans.data || []).map((p) => ({
                    ...p,
                    // Plan.jsx reads these in snake_case.
                    base_amount: p.baseAmount,
                    start_date: p.startDate,
                    end_date: p.endDate,
                  })),
                }
              : { data: [], totalSpent: 0 },
          },
        };
        dispatch({
          type: "GET_USER_DETAILS",
          data: shaped,
        });
        return shaped;
      })
      .catch((err) => {
        return false;
      });
  };
}

// A host's parlour-sales report (gross / platform fee / host payout per parlour
// + totals). New in the Admin — legacy only exposed this host-facing on mobile.
export function getUserParlourSales(id) {
  return () =>
    axios
      .get(`api/v1/admin/users/${id}/parlour-sales`)
      .then((res) => res.data)
      .catch(() => null);
}

// Raw AWS credentials are gone by design — the new API issues short-lived
// presigned upload URLs instead (`POST /api/v1/media/upload-url`). That is a
// different upload PATTERN, not just a different URL.
//
// The Headline and EditProfile forms have been rewired to `uploadViaPresign`
// (api/upload.js) and no longer call this. Only the Podcast screens still do,
// and podcast creation has no endpoint either — see `createPodcast`.
export function getAwsCredentials(id) {
  return (dispatch) => {
    return Promise.resolve(false);
  };
}

/**
 * Tell the operator that a control isn't backed by the new API, instead of
 * resolving `false` and looking like a dead button.
 *
 * A silent no-op is indistinguishable from a bug: it is exactly what "I press
 * Create and nothing happens" felt like on the Headline form, which turned out
 * to be a real failure. Anything that genuinely cannot work yet should SAY so —
 * the pattern `createSchedulerParlour` already uses.
 */
function unsupported(message) {
  return () => {
    toast(message, { type: "info", autoClose: 8000 });
    return Promise.resolve(false);
  };
}

// Projects admin CRUD was not ported (no `/admin/projects` endpoints exist in
// the new API). Projects are a legacy-only feature: 71 rows survive in the DB
// but the v2 app has no project surface at all, so there is nothing to manage.
export function projectDetail(id) {
  return unsupported("Projects aren't available in the new Admin Site.");
}

export function deleteProjects(projectId) {
  return unsupported("Projects aren't available in the new Admin Site.");
}

// EditProfile still builds the legacy snake_case payload. The new API's DTO is
// camelCase and Zod STRIPS unknown keys rather than rejecting them — so
// `profile_image`, `website_url` and `video_links` were silently discarded
// while the request still returned 200. Staff saw "saved" and the avatar,
// website and video links never changed. Same failure the Create Headline form
// had; translate at this adapter boundary, per this module's contract.
function toApiUserPayload(data) {
  const payload = {};
  const copy = ["name", "bio", "city", "state", "zipcode", "genreIds", "roleIds", "active"];
  for (const key of copy) if (data[key] !== undefined) payload[key] = data[key];
  if (data.profile_image !== undefined) payload.profileImageUrl = data.profile_image;
  if (data.cover_image !== undefined) payload.coverImageUrl = data.cover_image;
  if (data.website_url !== undefined) payload.websiteUrl = data.website_url;
  if (data.video_url !== undefined) payload.videoUrl = data.video_url;
  if (data.instagram_url !== undefined) payload.instagramUrl = data.instagram_url;
  if (data.tiktok_url !== undefined) payload.tiktokUrl = data.tiktok_url;
  if (data.soundcloud_url !== undefined) payload.soundcloudUrl = data.soundcloud_url;
  if (data.video_links !== undefined) payload.videoLinks = data.video_links;
  if (data.latitude !== undefined && data.latitude !== "") payload.latitude = data.latitude;
  if (data.longitude !== undefined && data.longitude !== "") payload.longitude = data.longitude;
  return payload;
}

export function userUpdate(userId, data) {
  return (dispatch) => {
    return axios
      .patch(`api/v1/admin/users/${userId}`, toApiUserPayload(data))
      .then((res) => {
        dispatch({
          type: "USER_UPDATE",
          data: res,
        });
        return res;
      })
      .catch((error) => {
        return error && error.response;
      });
  };
}

export function searchUser(searchData, page, limit, sortBy, sortDir) {
  let url = `api/v1/admin/users?page=${page ? page : Numbers.one}&limit=${
    limit ? limit : Numbers.ten
  }`;
  // `search` is `min(1)` server-side, so appending it unconditionally made the
  // X-clear button (`searchUser("")`) 400 and empty the table. An empty term is
  // simply "no filter".
  const term = (searchData || "").trim();
  if (term) url += `&search=${encodeURIComponent(term)}`;
  // Sorting was accepted by the caller and silently dropped here, so clicking a
  // column header while searching re-fetched unsorted.
  if (sortBy) url += `&sortBy=${sortBy}&sortDir=${sortDir ? "asc" : "desc"}`;
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        dispatch({
          type: "GET_SEARCH_USER",
          data: res.data,
        });
        return withLegacyMeta(res.data);
      })
      .catch((err) => {
        return false;
      });
  };
}

export function filterUser(args) {
  let {
    city,
    state,
    zipcode,
    roleIds,
    profile_type,
    start_date,
    end_date,
    page,
    limit,
    // Forwarded by Listing.jsx and destructured nowhere until now, so sorting
    // was silently discarded the moment any filter was active.
    sortBy,
    sortDir,
  } = args;
  let url = `api/v1/admin/users?page=${page ? page : Numbers.one}&limit=${
    limit ? limit : Numbers.ten
  }`;
  if (city) url = `${url}&city=${city}`;
  if (state) url = `${url}&state=${state}`;
  if (zipcode) url = `${url}&zipcode=${zipcode}`;
  if (start_date && end_date) url = `${url}&startDate=${start_date}&endDate=${end_date}`;
  if (roleIds && roleIds.length) url = `${url}&roleIds=${roleIds.join(",")}`;
  if (profile_type) url = `${url}&featured=${profile_type === "Featured" ? "true" : "false"}`;
  if (sortBy) url = `${url}&sortBy=${sortBy}&sortDir=${sortDir ? "asc" : "desc"}`;
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        dispatch({
          type: "FILTER_USER",
          data: res.data,
        });
        return withLegacyMeta(res.data);
      })
      .catch((err) => {
        return false;
      });
  };
}

export function searchUserByName(searchData) {
  let url = `api/v1/admin/users?search=${searchData}&limit=20`;
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        return res.data;
      })
      .catch((err) => {
        return false;
      });
  };
}

export function deleteAudioTrack(audioId, userId) {
  // Only the Users → track-delete call site passes userId; the Podcasts
  // screen's own track delete goes through the id-based delete instead.
  return (dispatch) => {
    return axios
      .delete(`api/v1/admin/users/${userId}/tracks/${audioId}`)
      .then((res) => {
        dispatch({
          type: "DELETE_USER_MEDIA",
          data: res.data,
        });
        return res.data;
      })
      .catch((error) => {
        return error && error.response;
      });
  };
}


// Podcast creation (with track upload) was not ported — it depends on the
// Podcast creation has no endpoint in the new API (only GET/PATCH/DELETE), and
// podcasts are a legacy-only feature: the table holds 0 rows and the v2 app has
// no podcast surface at all. Says so rather than looking like a dead button.
export function createPodcast(data) {
  return unsupported("Creating podcasts isn't supported in the new Admin Site.");
}

// The headline form (containers/headline/Listing.jsx) still speaks the legacy
// snake_case payload; the new API's Zod DTO is camelCase and REJECTS the old
// names outright — `video_url` never satisfies the `videoUrl`-is-required rule
// for a video headline, so every video/image headline 400'd and the form's
// error toast was handed the error OBJECT, which renders as nothing. That is
// the "I press Create and nothing happens" report: the request was failing
// silently. Translate at this adapter boundary, per this module's contract of
// keeping container signatures stable.
export function createHeadline(data) {
  return (dispatch) => {
    const payload = {
      title: data.title,
      type: data.type,
      ...(data.description ? { description: data.description } : {}),
      ...(data.website_link ? { websiteLink: data.website_link } : {}),
      ...(data.video_url ? { videoUrl: data.video_url } : {}),
      ...(data.news_photo?.length ? { newsPhoto: data.news_photo } : {}),
    };
    return axios
      .post(`api/v1/admin/news`, payload)
      .then((res) => {
        dispatch({
          type: "USER_CREATE_HEADLINE",
          data: res,
        });
        return res;
      })
      .catch((error) => {
        return error && error.response;
      });
  };
}

// Imported by Listing.jsx but not actually called from its render logic —
// kept as a working alias of userList() rather than removed, since sort-by
// isn't implemented server-side yet either way.
export function sortUserList(page) {
  return userList(page);
}


export function dashboardStatics() {
  return (dispatch) => {
    return axios
      .get(`api/v1/admin/dashboard`)
      .then((res) => {
        // Dashboard.jsx destructures this as a legacy-style 2-tuple:
        // [{registered_users, registered_headlines, total_featured,
        // total_headline_count, total_bulletin_count}, roleBreakdownArray].
        // Reshape the new API's flat object into that exact shape here, at
        // the wrapper boundary, so the component doesn't need to change.
        const s = res.data;
        const shaped = [
          {
            registered_users: s.totalUsers,
            registered_headlines: s.totalHeadlines,
            total_featured: s.featuredUsers,
            // Legacy's `total_headline_count` is a distinct 7-day-window
            // metric, not the all-time headline count — see
            // AdminDashboardService.stats()'s `recentHeadlines`.
            total_headline_count: s.recentHeadlines,
            total_bulletin_count: s.totalBulletins,
          },
          (s.roleBreakdown || []).map((r) => ({
            role_id: r.roleId,
            title: r.title,
            member_count: r.count,
          })),
        ];
        dispatch({
          type: "DASHBOARD_STATICS",
          data: shaped,
        });
        return shaped;
      })
      .catch((err) => {
        return false;
      });
  };
}

export function getPodcastList(page, sortBy, sortDir = false, limit = 10) {
  let url = `api/v1/admin/podcasts?page=${page}&limit=${limit}`;
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        dispatch({
          type: "GET_PODCAST_LIST",
          data: res.data,
        });
        return withLegacyMeta(res.data);
      })
      .catch((err) => {
        return false;
      });
  };
}

export function getNewsList(page, sortBy, sortDir = false, limit = 10) {
  let url = `api/v1/admin/news?page=${page}&limit=${limit}`;
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        dispatch({
          type: "GET_NEWS_LIST",
          data: res.data,
        });
        return withLegacyMeta(res.data);
      })
      .catch((err) => {
        return false;
      });
  };
}

export function getPodcastDetails(Id) {
  return (dispatch) => {
    return axios
      .get(`api/v1/admin/podcasts/${Id}`)
      .then((res) => {
        dispatch({
          type: "GET_PODCAST_DETAILS",
          data: res.data,
        });
        return res.data;
      })
      .catch((err) => {
        return false;
      });
  };
}

export function updatePodcast(Id, data) {
  // Both callers build the legacy shape `{ name, user_id, is_show, audio_url }`.
  // `AdminUpdatePodcastSchema` is `{ name, isShow }` and `.strict()`, so the raw
  // payload 400'd on every save — and the containers gate success on
  // `status === 201` while PATCH answers 200, so even a correct payload could
  // never reach their success branch. Translate what the API supports, and say
  // plainly what it does not rather than failing opaquely.
  const body = {};
  if (data && data.name !== undefined) body.name = data.name;
  if (data && data.is_show !== undefined) body.isShow = !!data.is_show;
  const dropsAudio = !!(data && data.audio_url && data.audio_url.length);
  const dropsOwner = !!(data && data.user_id !== undefined);
  return (dispatch) => {
    return axios
      .patch(`api/v1/admin/podcasts/${Id}`, body)
      .then((res) => {
        dispatch({
          type: "USER_UPDATE_PODCAST",
          data: res,
        });
        if (dropsAudio || dropsOwner) {
          toast(
            "Saved the name and show flag. Audio tracks and the owner can't be changed — the new API doesn't expose them.",
            { type: "warning", autoClose: 8000 }
          );
        }
        // Normalized so the containers' `status === 201` check succeeds; PATCH
        // legitimately answers 200.
        return { ...res, status: 201 };
      })
      .catch((error) => {
        const message =
          (error && error.response && error.response.data && error.response.data.message) ||
          "Couldn't save the podcast.";
        toast(message, { type: "error", autoClose: 8000 });
        return error && error.response;
      });
  };
}

export function deleteMediaViaUrl(Id) {
  // The new API only supports delete-by-id (no S3-key variant) — see
  // deleteAudioTrack for the id-based equivalent.
  return unsupported("Deleting media by URL isn't supported — use the track's Remove action.");
}

export function deletePodcast(Id) {
  return (dispatch) => {
    return axios
      .delete(`api/v1/admin/podcasts/${Id}`)
      .then((res) => {
        dispatch({
          type: "USER_DELETE_PODCAST",
          data: res,
        });
        return res;
      })
      .catch((error) => {
        return error && error.response;
      });
  };
}

export function deleteNews(Id) {
  return (dispatch) => {
    return axios
      .delete(`api/v1/admin/news/${Id}`)
      .then((res) => {
        dispatch({
          type: "USER_DELETE_NEWS",
          data: res,
        });
        return res;
      })
      .catch((error) => {
        return error && error.response;
      });
  };
}

export function supportList(page, limit) {
  return (dispatch) => {
    let url = `api/v1/admin/support?page=${page ? page : Numbers.one}&limit=${
      limit ? limit : Numbers.ten
    }`;
    return axios
      .get(url)
      .then((res) => {
        dispatch({
          type: "SUPPORT_LIST",
          data: res,
        });
        return res;
      })
      .catch((err) => {
        return false;
      });
  };
}

export function userEmailReply(id, message) {
  // Legacy shape was `{ id, message }`, sent to a create-style endpoint; the
  // new API replies to a specific ticket via `POST /admin/support/:id/reply`.
  // Takes the id explicitly — the previous caller passed an object built from a
  // DTO shape that no longer exists, so `data.id` was always undefined and every
  // reply hit `/support/undefined/reply` and 400'd on the ParseIntPipe.
  return (dispatch) => {
    return axios
      .post(`api/v1/admin/support/${id}/reply`, { message })
      .then((res) => {
        dispatch({
          type: "USER_EMAIL_REPLY",
          data: res,
        });
        return res;
      })
      .catch((error) => {
        return error && error.response;
      });
  };
}

export function removeSupportMessages(Id) {
  return (dispatch) => {
    return axios
      .delete(`api/v1/admin/support/${Id}`)
      .then((res) => {
        dispatch({
          type: "ADMIN_DELETE_SUPPORT_MESSAGES",
          data: res,
        });
        return res;
      })
      .catch((error) => {
        return error && error.response;
      });
  };
}

// Staff's own password change. Was stubbed on the same wrong premise as
// `userResetPassword` — staff are ordinary users, so `/auth/password/change`
// covers them. Stubbed, the Change Password screen's button did nothing.
//
// ChangePassword.jsx builds `{ current_password, password, user_id }`; the API
// takes `{ currentPassword, newPassword }` and derives the user from the bearer
// (so `user_id` is neither needed nor trusted from the client). It reads
// `res.data.msg` for its toast, which the new API doesn't return — synthesize
// one rather than let it toast `undefined`.
export function changeAdminPasswordApi(data) {
  return (dispatch) => {
    return axios
      .post(`api/v1/auth/password/change`, {
        currentPassword: data.current_password,
        newPassword: data.password,
      })
      .then((res) => ({
        ...res,
        data: { ...(res.data || {}), msg: "Your password has been changed successfully!" },
      }))
      .catch((error) => {
        // Surface the API's reason (e.g. "Current password is incorrect")
        // under the `msg` key the screen renders.
        const response = error && error.response;
        if (!response) return false;
        return {
          ...response,
          data: {
            ...(response.data || {}),
            msg: (response.data && response.data.message) || "Could not change your password.",
          },
        };
      });
  };
}

// The notification bell (components/shared/pageComponent/NotificationBar.jsx)
// still reads the legacy shape: `{ rows, un_read, totalPage }` with each item
// carrying `message`. The new API answers `{ data, meta }` with `body`.
//
// Dispatching the raw response CRASHED the whole Admin Site the moment anyone
// clicked the bell — `notifications.forEach(n => n.rows.map(...))` threw
// "Cannot read properties of undefined (reading 'map')", and because the bell
// lives in the dashboard shell (outside the per-screen boundary) it took the
// page down with it. Verified by clicking it. Translate here, per this module's
// contract of keeping container signatures stable.
export function getNotificationList(page, limit, read) {
  // The STAFF feed, not a user's own notifications. `/api/v1/notifications` now
  // filters on `to_user_id`, and every staff row ("X has registered") has a NULL
  // recipient — so pointing the bell there left it permanently empty.
  const url = `api/v1/admin/notifications?page=${page}&limit=${limit}`;
  return (dispatch) => {
    return axios
      .get(url)
      .then((res) => {
        const body = (res && res.data) || {};
        const rows = (body.data || []).map((n) => ({
          ...n,
          // The bell renders `message` via dangerouslySetInnerHTML.
          message: n.body,
        }));
        const shaped = {
          rows,
          un_read: rows.some((n) => !n.read),
          totalPage: (body.meta && body.meta.totalPages) || 0,
        };
        dispatch({
          type: "NOTIFICATION_LIST_ACTION",
          data: shaped,
        });
        return shaped;
      })
      .catch((err) => {
        return false;
      });
  };
}

export function dashboardGraphData(data) {
  const params = new URLSearchParams();
  if (data?.filterType) params.set("filterType", data.filterType);
  if (data?.start_date) params.set("startDate", data.start_date);
  if (data?.end_date) params.set("endDate", data.end_date);
  return (dispatch) => {
    return axios
      .get(`api/v1/admin/dashboard/growth?${params.toString()}`)
      .then(async (res) => {
        // Dashboard.jsx expects { graphData: { data1, data2 }, totalUsers }
        // where data1 is a per-period series and data2 is the signup-count
        // series this endpoint returns.
        const points = res.data || [];
        const data2 = points.map((p) => ({ count: p.users }));
        const data1 = points.map((p) => ({
          lable: formatDate(p.period),
          labelDate: p.period,
          // The signup series carries no per-period revenue, and this used to
          // render a hardcoded "$0" as though it were a measurement. Null is
          // honest; the chart's revenue line simply has no data.
          amount: null,
        }));
        const shaped = {
          graphData: { data1, data2 },
          totalUsers: points.reduce((sum, p) => sum + p.users, 0),
          // Real gross revenue, not a placeholder: the same aggregate the
          // Transactions screen shows. Best-effort — the graph must still render
          // if this call fails.
          membershipRevenue: await axios
            .get(`api/v1/admin/transactions/summary`)
            .then((r) => `$${r.data.grossRevenue}`)
            .catch(() => null),
        };
        dispatch({
          type: "DASHBOARD_GRAPH_DATA",
          data: shaped,
        });
        return shaped;
      })
      .catch((err) => {
        return false;
      });
  };
}

export function deleteUser(id) {
  return (dispatch) => {
    return axios
      .delete(`api/v1/admin/users/${id}`)
      .then((res) => {
        dispatch({
          type: "ADMIN_DELETE_USER",
          data: res,
        });
        return res;
      })
      .catch((error) => {
        return error && error.response;
      });
  };
}

export default {
  userLogin,
};
